// MAXtimate Calculator Component Module
// Main calculator interface component

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Calculator, Plus, Info, Music2, Hash, Users, DollarSign, Sparkles } from 'lucide-react';
import { MaxSpinner } from "@/components/ui/max-spinner";
import { Link } from 'wouter';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { apiRequest } from '@/lib/queryClient';
import { PackageDetailsModal } from '@/components/package-details-modal';
import { getPackageConfig, formatPrice, type PackageType } from '@/lib/package-config';
import { CareerStageModal } from '@/components/career-stage-modal';
import { ShowCountModal } from '@/components/show-count-modal';
import { useToast } from '@/hooks/use-toast';
import { PlanItemsList } from './PlanItemsList';
import { PlanSummary } from './PlanSummary';
import { PlanDeliverablesSimple } from './PlanDeliverablesSimple';
import { BudgetBuilder } from './BudgetBuilder';

interface PackagePricing {
  setCore: number;
  setAudio: number;
  setVideo: number;
  // Pricing for base+1 shows when there's a remainder
  basePlusOnePricing?: {
    setCore: number;
    setAudio: number;
    setVideo: number;
  };
}

interface PlanItem {
  id: string;
  careerStage: string;
  numberOfShows: number;
  numberOfArtists: number;
  showsPerArtist: number;
  artistsWithBaseShows?: number;
  artistsWithExtraShow?: number;
  packageTier: 'set-core' | 'set-audio' | 'set-video';
  pricePerShow: number;
  totalPrice: number;
}

interface SimplifiedPackageCardProps {
  packageType: PackageType;
  price: number;
  pricePerShow: number;
  showCount: number;
  onDetailsClick: (packageType: PackageType) => void;
  onAddToPlan: (packageType: PackageType) => void;
  isCalculating?: boolean;
}

function SimplifiedPackageCard({
  packageType,
  price,
  pricePerShow,
  showCount,
  onDetailsClick,
  onAddToPlan,
  isCalculating = false
}: SimplifiedPackageCardProps) {
  const config = getPackageConfig(packageType);
  const Icon = config.icon;
  
  // Use the same color classes as the full package cards
  const colorClasses = {
    primary: {
      'set-core': 'text-gray-600',
      'set-audio': 'text-blue-600',
      'set-video': 'text-purple-600',
      'custom': 'text-green-600'
    },
    border: {
      'set-core': 'border-gray-200',
      'set-audio': 'border-blue-200',
      'set-video': 'border-purple-200',
      'custom': 'border-green-200'
    }
  };

  const buttonClasses = {
    'set-core': 'text-gray-600 hover:text-gray-700 hover:bg-gray-50',
    'set-audio': 'text-blue-600 hover:text-blue-700 hover:bg-blue-50',
    'set-video': 'text-purple-600 hover:text-purple-700 hover:bg-purple-50',
    'custom': 'text-green-600 hover:text-green-700 hover:bg-green-50'
  };

  return (
    <div className="relative"> {/* Remove top padding so card tops align */}
      <Card className={cn(
        "relative flex flex-col h-full border-0", // Remove default border since we're applying full background
        // Apply the gradient/background to the entire card
        config.colorScheme.gradient 
          ? `bg-gradient-to-br ${config.colorScheme.gradient}` 
          : "bg-white border border-gray-200" // White background with subtle border for GA package
      )}>
        {/* Package Badge */}
        {config.badge && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
            <Badge className={cn(
              "text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap",
              packageType === 'set-core' && "bg-gray-600",
              packageType === 'set-audio' && "bg-blue-600",
              packageType === 'set-video' && "bg-gradient-to-r from-purple-600 to-blue-600",
              packageType === 'custom' && "bg-green-600"
            )}>
              {config.badge}
            </Badge>
          </div>
        )}
        
        {/* Header */}
        <CardHeader className="text-center pb-4 pt-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Icon className={cn("w-5 h-5", colorClasses.primary[packageType])} />
            <h3 className={cn(
              "text-lg font-semibold",
              colorClasses.primary[packageType]
            )}>
              {config.name}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {config.subtitle}
          </p>
        </CardHeader>
        
        <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
          {/* Pricing */}
          <div className="text-center">
            <div className={cn("text-2xl font-bold mb-1", colorClasses.primary[packageType])}>
              {isCalculating ? (
                <div className="flex items-center justify-center">
                  <MaxSpinner className="w-5 h-5" />
                </div>
              ) : (
                formatPrice(price)
              )}
            </div>
            {!isCalculating && showCount > 0 && (
              <div className="text-sm text-muted-foreground">
                {formatPrice(pricePerShow)} per show
              </div>
            )}
          </div>
          
          {/* Buttons */}
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDetailsClick(packageType)}
              className={cn(
                "w-full transition-all duration-200",
                // Different button styling based on package background
                packageType === 'set-core'
                  ? "bg-gray-50 hover:bg-gray-100 border-gray-300 hover:border-gray-400"
                  : "bg-white/80 hover:bg-white border-white/50 hover:border-white",
                buttonClasses[packageType]
              )}
            >
              <Info className="w-4 h-4 mr-2" />
              What's in {config.name}?
            </Button>

            <Button
              size="sm"
              onClick={() => onAddToPlan(packageType as 'set-core' | 'set-audio' | 'set-video')}
              disabled={isCalculating}
              className={cn(
                "w-full transition-all duration-200",
                packageType === 'set-core' && "bg-gray-600 hover:bg-gray-700",
                packageType === 'set-audio' && "bg-blue-600 hover:bg-blue-700",
                packageType === 'set-video' && "bg-purple-600 hover:bg-purple-700"
              )}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Plan
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function MAXtimateCalculator() {
  const { user } = useAuth();
  const { toast } = useToast();

  // Basic inputs
  const [careerStage, setCareerStage] = useState('');
  const [numberOfShows, setNumberOfShows] = useState(1);
  const [numberOfArtists, setNumberOfArtists] = useState(1);

  // Results
  const [packagePricing, setPackagePricing] = useState<PackagePricing | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Plan state
  const [planItems, setPlanItems] = useState<PlanItem[]>([]);

  // Budget Builder state
  const [totalBudget, setTotalBudget] = useState<number>(50000);
  const [selectedCareerStages, setSelectedCareerStages] = useState<string[]>([]);
  const [budgetDistribution, setBudgetDistribution] = useState<string>('even');
  const [defaultPackage, setDefaultPackage] = useState<'set-core' | 'set-audio' | 'set-video'>('set-audio');
  const [targetShowsPerArtist, setTargetShowsPerArtist] = useState<number>(5);
  const [strictMode, setStrictMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Modal state
  const [selectedPackageForDetails, setSelectedPackageForDetails] = useState<PackageType | null>(null);
  const [isCareerStageModalOpen, setIsCareerStageModalOpen] = useState(false);
  const [isShowCountModalOpen, setIsShowCountModalOpen] = useState(false);

  // Career stages
  const careerStages = [
    'Developing',
    'Emerging',
    'Established 1',
    'Established 2',
    'Established 3',
    'Mainstream 1',
    'Mainstream 2',
    'Mainstream 3',
    'Mainstream 4'
  ];

  // Clear pricing when inputs change to prevent showing incorrect prices
  useEffect(() => {
    if (packagePricing) {
      setPackagePricing(null);
    }
  }, [careerStage, numberOfShows, numberOfArtists]);

  // Helper function to calculate weighted price based on show distribution
  const calculateWeightedPrice = (
    packageTier: 'set-core' | 'set-audio' | 'set-video',
    pricing: PackagePricing,
    artistsWithBase: number,
    artistsWithExtra: number
  ): number => {
    // Get base price per artist
    let basePricePerArtist = 0;
    switch (packageTier) {
      case 'set-core':
        basePricePerArtist = pricing.setCore;
        break;
      case 'set-audio':
        basePricePerArtist = pricing.setAudio;
        break;
      case 'set-video':
        basePricePerArtist = pricing.setVideo;
        break;
    }

    // If no remainder, simple calculation
    if (artistsWithExtra === 0 || !pricing.basePlusOnePricing) {
      return basePricePerArtist * (artistsWithBase + artistsWithExtra);
    }

    // Get extra price per artist (for base+1 shows)
    let extraPricePerArtist = 0;
    switch (packageTier) {
      case 'set-core':
        extraPricePerArtist = pricing.basePlusOnePricing.setCore;
        break;
      case 'set-audio':
        extraPricePerArtist = pricing.basePlusOnePricing.setAudio;
        break;
      case 'set-video':
        extraPricePerArtist = pricing.basePlusOnePricing.setVideo;
        break;
    }

    // Calculate weighted total
    return (artistsWithBase * basePricePerArtist) + (artistsWithExtra * extraPricePerArtist);
  };

  const handleCalculate = async () => {
    if (!careerStage) {
      setError('Please select an artist career stage');
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      // Calculate shows distribution
      const baseShows = Math.floor(numberOfShows / numberOfArtists);
      const remainder = numberOfShows % numberOfArtists;

      // Get pricing for base show count
      const baseResponse = await apiRequest('/api/calculator/calculate', {
        method: 'POST',
        body: {
          careerStage,
          numberOfShows: baseShows,
          mediaObjective: 'No Media',
          marketSize: 'Medium',
          venueCapacity: 5000,
        }
      });

      let finalPricing = baseResponse.packagePricing;

      // If there's a remainder, also get pricing for base+1 shows
      if (remainder > 0 && baseShows > 0) {
        const extraResponse = await apiRequest('/api/calculator/calculate', {
          method: 'POST',
          body: {
            careerStage,
            numberOfShows: baseShows + 1,
            mediaObjective: 'No Media',
            marketSize: 'Medium',
            venueCapacity: 5000,
          }
        });

        // Store both pricing tiers
        finalPricing = {
          ...baseResponse.packagePricing,
          basePlusOnePricing: extraResponse.packagePricing
        };
      }

      if (finalPricing) {
        setPackagePricing(finalPricing);
      } else {
        setError('Unable to calculate pricing for the selected criteria');
      }

      // Track calculator usage
    } catch (err) {
      setError('Failed to calculate pricing. Please try again.');
      console.error('Calculator error:', err);
    } finally {
      setIsCalculating(false);
    }
  };

  const resetCalculator = () => {
    setCareerStage('');
    setNumberOfShows(1);
    setNumberOfArtists(1);
    setPackagePricing(null);
    setError(null);
  };

  // Plan management functions
  const addToPlan = (packageTier: 'set-core' | 'set-audio' | 'set-video') => {
    if (!packagePricing || !careerStage || numberOfShows < 1) {
      toast({
        title: "Cannot add to plan",
        description: "Please calculate pricing first",
        variant: "destructive",
      });
      return;
    }

    // Calculate show distribution
    const baseShows = Math.floor(numberOfShows / numberOfArtists);
    const remainder = numberOfShows % numberOfArtists;
    const artistsWithBase = numberOfArtists - remainder;
    const artistsWithExtra = remainder;

    // Calculate weighted total price
    const totalPrice = calculateWeightedPrice(
      packageTier,
      packagePricing,
      artistsWithBase,
      artistsWithExtra
    );

    const pricePerShow = totalPrice / numberOfShows;

    const newItem: PlanItem = {
      id: `${Date.now()}-${Math.random()}`,
      careerStage,
      numberOfShows,
      numberOfArtists,
      showsPerArtist: baseShows,
      artistsWithBaseShows: artistsWithBase,
      artistsWithExtraShow: artistsWithExtra,
      packageTier,
      pricePerShow: Math.round(pricePerShow),
      totalPrice,
    };

    setPlanItems([...planItems, newItem]);

    const packageConfig = getPackageConfig(packageTier);
    const artistText = numberOfArtists === 1 ? 'artist' : 'artists';

    // Enhanced toast message showing distribution
    let description = `${numberOfShows} ${careerStage} show${numberOfShows > 1 ? 's' : ''} × ${numberOfArtists} ${artistText}`;
    if (remainder > 0) {
      description += ` (${artistsWithBase}×${baseShows} + ${artistsWithExtra}×${baseShows + 1})`;
    }
    description += ` with ${packageConfig.name}`;

    toast({
      title: "Added to Plan",
      description,
    });
  };

  const removeFromPlan = (itemId: string) => {
    setPlanItems(planItems.filter(item => item.id !== itemId));
    toast({
      title: "Removed from Plan",
      description: "Item removed successfully",
    });
  };

  const clearPlan = () => {
    setPlanItems([]);
    toast({
      title: "Plan Cleared",
      description: "All items have been removed from the plan",
    });
  };

  const updatePlanItem = async (itemId: string, updates: Partial<PlanItem>) => {
    // Find the item to update
    const item = planItems.find((i) => i.id === itemId);
    if (!item) return;

    // Use updated values or fall back to current values
    const updatedCareerStage = updates.careerStage || item.careerStage;
    const updatedNumberOfShows = updates.numberOfShows || item.numberOfShows;
    const updatedNumberOfArtists = updates.numberOfArtists || item.numberOfArtists;
    const updatedPackageTier = updates.packageTier || item.packageTier;

    try {
      // Calculate shows distribution
      const baseShows = Math.floor(updatedNumberOfShows / updatedNumberOfArtists);
      const remainder = updatedNumberOfShows % updatedNumberOfArtists;

      // Get pricing for base show count
      const baseResponse = await apiRequest('/api/calculator/calculate', {
        method: 'POST',
        body: {
          careerStage: updatedCareerStage,
          numberOfShows: baseShows,
          mediaObjective: 'No Media',
          marketSize: 'Medium',
          venueCapacity: 5000,
        }
      });

      let finalPricing = baseResponse.packagePricing;

      // If there's a remainder, also get pricing for base+1 shows
      if (remainder > 0 && baseShows > 0) {
        const extraResponse = await apiRequest('/api/calculator/calculate', {
          method: 'POST',
          body: {
            careerStage: updatedCareerStage,
            numberOfShows: baseShows + 1,
            mediaObjective: 'No Media',
            marketSize: 'Medium',
            venueCapacity: 5000,
          }
        });

        finalPricing = {
          ...baseResponse.packagePricing,
          basePlusOnePricing: extraResponse.packagePricing
        };
      }

      // Calculate show distribution
      const artistsWithBase = updatedNumberOfArtists - remainder;
      const artistsWithExtra = remainder;

      // Calculate weighted total price
      const totalPrice = calculateWeightedPrice(
        updatedPackageTier,
        finalPricing,
        artistsWithBase,
        artistsWithExtra
      );

      const pricePerShow = totalPrice / updatedNumberOfShows;

      // Update the plan item with recalculated pricing
      const updatedItem: PlanItem = {
        ...item,
        careerStage: updatedCareerStage,
        numberOfShows: updatedNumberOfShows,
        numberOfArtists: updatedNumberOfArtists,
        showsPerArtist: baseShows,
        artistsWithBaseShows: artistsWithBase,
        artistsWithExtraShow: artistsWithExtra,
        packageTier: updatedPackageTier,
        pricePerShow: Math.round(pricePerShow),
        totalPrice,
      };

      // Update the plan items array
      setPlanItems(planItems.map((i) => (i.id === itemId ? updatedItem : i)));

      toast({
        title: "Item Updated",
        description: "Plan item updated successfully with recalculated pricing",
      });
    } catch (err) {
      toast({
        title: "Update Failed",
        description: "Failed to update item. Please try again.",
        variant: "destructive",
      });
      console.error('Update item error:', err);
      throw err; // Re-throw to let the PlanItemsList component handle it
    }
  };

  // Helper function to get distribution weights
  const getDistributionWeights = (distribution: string, count: number): number[] => {
    // Distribution patterns (from lowest to highest career stage)
    const patterns: { [key: string]: number[] } = {
      'even': Array(count).fill(1 / count),
      'favor-lower': count === 2 ? [0.6, 0.4] : count === 3 ? [0.5, 0.3, 0.2] : [0.4, 0.3, 0.2, 0.1],
      'favor-higher': count === 2 ? [0.4, 0.6] : count === 3 ? [0.2, 0.3, 0.5] : [0.1, 0.2, 0.3, 0.4],
      'heavy-lower': count === 2 ? [0.7, 0.3] : count === 3 ? [0.6, 0.3, 0.1] : [0.5, 0.3, 0.15, 0.05],
      'heavy-higher': count === 2 ? [0.3, 0.7] : count === 3 ? [0.1, 0.3, 0.6] : [0.05, 0.15, 0.3, 0.5],
    };

    // Get pattern or default to even split
    const pattern = patterns[distribution] || patterns['even'];

    // If we have more stages than the pattern, extend with even distribution
    if (count > pattern.length) {
      return Array(count).fill(1 / count);
    }

    // If we have fewer stages, take the first N weights and normalize
    const weights = pattern.slice(0, count);
    const sum = weights.reduce((a, b) => a + b, 0);
    return weights.map(w => w / sum);
  };

  // Helper function to find optimal show count for a given budget and stage
  const findOptimalShowCount = async (
    stage: string,
    budget: number,
    packageTier: 'set-core' | 'set-audio' | 'set-video',
    targetShows: number
  ): Promise<{
    numberOfShows: number;
    numberOfArtists: number;
    baseShows: number;
    artistsWithBase: number;
    artistsWithExtra: number;
    totalPrice: number;
    pricePerShow: number;
  } | null> => {
    // Start with 1 show and increment to find max affordable
    let bestFit: {
      numberOfShows: number;
      numberOfArtists: number;
      baseShows: number;
      artistsWithBase: number;
      artistsWithExtra: number;
      totalPrice: number;
      pricePerShow: number;
    } | null = null;

    // Try increasing by 1 show at a time until we exceed budget
    let currentShows = 1;
    let lastAffordable = 0;

    while (true) {
      const numberOfArtists = Math.ceil(currentShows / targetShows);
      const baseShows = Math.floor(currentShows / numberOfArtists);
      const remainder = currentShows % numberOfArtists;
      const artistsWithBase = numberOfArtists - remainder;
      const artistsWithExtra = remainder;

      // Fetch pricing for base show count
      const baseResponse = await apiRequest('/api/calculator/calculate', {
        method: 'POST',
        body: {
          careerStage: stage,
          numberOfShows: baseShows,
          mediaObjective: 'No Media',
          marketSize: 'Medium',
          venueCapacity: 5000,
        }
      });

      let finalPricing = baseResponse.packagePricing;

      // If there's a remainder, also get pricing for base+1 shows
      if (remainder > 0 && baseShows > 0) {
        const extraResponse = await apiRequest('/api/calculator/calculate', {
          method: 'POST',
          body: {
            careerStage: stage,
            numberOfShows: baseShows + 1,
            mediaObjective: 'No Media',
            marketSize: 'Medium',
            venueCapacity: 5000,
          }
        });

        finalPricing = {
          ...baseResponse.packagePricing,
          basePlusOnePricing: extraResponse.packagePricing
        };
      }

      // Calculate weighted total price
      const totalPrice = calculateWeightedPrice(
        packageTier,
        finalPricing,
        artistsWithBase,
        artistsWithExtra
      );

      // Check if this fits in budget
      if (totalPrice <= budget) {
        bestFit = {
          numberOfShows: currentShows,
          numberOfArtists,
          baseShows,
          artistsWithBase,
          artistsWithExtra,
          totalPrice,
          pricePerShow: totalPrice / currentShows,
        };
        lastAffordable = currentShows;
        currentShows += 1; // Try one more show
      } else {
        // Exceeded budget, stop here
        break;
      }

      // Safety check: don't go beyond 200 shows
      if (currentShows > 200) break;
    }

    return bestFit;
  };

  // Generate plan from budget
  const generateFromBudget = async (params: {
    totalBudget: number;
    selectedStages: string[];
    distribution: string;
    packageTier: 'set-core' | 'set-audio' | 'set-video';
    strictMode: boolean;
    targetShowsPerArtist: number;
  }) => {
    setIsGenerating(true);

    try {
      const { totalBudget, selectedStages, distribution, packageTier, strictMode, targetShowsPerArtist } = params;

      // Get distribution weights
      const weights = getDistributionWeights(distribution, selectedStages.length);

      // Calculate initial budget allocation per stage
      let budgetAllocations = weights.map(w => totalBudget * w);
      let activeStages = selectedStages.map((stage, idx) => ({
        stage,
        budget: budgetAllocations[idx],
        weight: weights[idx],
      }));

      // Find optimal show counts for each stage
      const stageResults = await Promise.all(
        activeStages.map(async (s) => {
          const result = await findOptimalShowCount(s.stage, s.budget, packageTier, targetShowsPerArtist);
          return {
            ...s,
            result,
          };
        })
      );

      // Smart rebalancing: filter out unaffordable stages (those that couldn't afford even 5 shows)
      let affordableStages = stageResults.filter(s => s.result !== null);

      // Redistribute budget if some stages were removed
      while (affordableStages.length < stageResults.length && affordableStages.length > 0) {
        // Recalculate weights proportionally
        const totalWeight = affordableStages.reduce((sum, s) => sum + s.weight, 0);
        const newWeights = affordableStages.map(s => s.weight / totalWeight);

        // Recalculate budget allocations
        budgetAllocations = newWeights.map(w => totalBudget * w);

        // Recalculate optimal show counts with new budgets
        const rebalancedResults = await Promise.all(
          affordableStages.map(async (s, idx) => {
            const newBudget = budgetAllocations[idx];
            const result = await findOptimalShowCount(s.stage, newBudget, packageTier, targetShowsPerArtist);
            return {
              stage: s.stage,
              budget: newBudget,
              weight: newWeights[idx],
              result,
            };
          })
        );

        // Check if rebalancing helped
        const stillAffordable = rebalancedResults.filter(s => s.result !== null);
        if (stillAffordable.length === affordableStages.length) {
          // No change, stop rebalancing
          affordableStages = stillAffordable;
          break;
        }
        affordableStages = stillAffordable;
      }

      // If no stages are affordable, show error
      if (affordableStages.length === 0) {
        toast({
          title: "Budget Too Low",
          description: "The selected budget is too low for any of the selected career stages. Please increase your budget or select lower career stages.",
          variant: "destructive",
        });
        return;
      }

      // Calculate total with optimal show counts
      let currentTotal = affordableStages.reduce((sum, s) => sum + (s.result?.totalPrice || 0), 0);

      // Budget matching: try to maximize budget usage
      const maxBudget = strictMode ? totalBudget : totalBudget * 1.05;

      // Try to get closer to budget by adding individual shows
      if (currentTotal < totalBudget) {
        let improved = true;

        while (improved && currentTotal < maxBudget) {
          improved = false;
          let bestAddition: { stageIdx: number; newResult: any; newTotal: number; distanceToTarget: number } | null = null;

          // For each stage, calculate the cost of adding 1 more show
          for (let i = 0; i < affordableStages.length; i++) {
            const stage = affordableStages[i];
            const result = stage.result!;

            // Calculate new show distribution with +1 show
            const newNumberOfShows = result.numberOfShows + 1;
            const newNumberOfArtists = Math.ceil(newNumberOfShows / targetShowsPerArtist);
            const newBaseShows = Math.floor(newNumberOfShows / newNumberOfArtists);
            const newRemainder = newNumberOfShows % newNumberOfArtists;
            const newArtistsWithBase = newNumberOfArtists - newRemainder;
            const newArtistsWithExtra = newRemainder;

            try {
              // Fetch pricing for new base show count
              const baseResponse = await apiRequest('/api/calculator/calculate', {
                method: 'POST',
                body: {
                  careerStage: stage.stage,
                  numberOfShows: newBaseShows,
                  mediaObjective: 'No Media',
                  marketSize: 'Medium',
                  venueCapacity: 5000,
                }
              });

              let finalPricing = baseResponse.packagePricing;

              // If there's a remainder, also get pricing for base+1 shows
              if (newRemainder > 0 && newBaseShows > 0) {
                const extraResponse = await apiRequest('/api/calculator/calculate', {
                  method: 'POST',
                  body: {
                    careerStage: stage.stage,
                    numberOfShows: newBaseShows + 1,
                    mediaObjective: 'No Media',
                    marketSize: 'Medium',
                    venueCapacity: 5000,
                  }
                });

                finalPricing = {
                  ...baseResponse.packagePricing,
                  basePlusOnePricing: extraResponse.packagePricing
                };
              }

              // Calculate weighted total price
              const newTotalPrice = calculateWeightedPrice(
                packageTier,
                finalPricing,
                newArtistsWithBase,
                newArtistsWithExtra
              );

              // Calculate new overall total
              const newOverallTotal = currentTotal - result.totalPrice + newTotalPrice;

              // Check if this addition fits within maxBudget
              if (newOverallTotal <= maxBudget) {
                // Calculate how close this gets us to the target
                const distanceToTarget = Math.abs(newOverallTotal - totalBudget);

                // Keep track of the best addition (closest to target)
                if (!bestAddition || distanceToTarget < bestAddition.distanceToTarget) {
                  bestAddition = {
                    stageIdx: i,
                    newResult: {
                      numberOfShows: newNumberOfShows,
                      numberOfArtists: newNumberOfArtists,
                      baseShows: newBaseShows,
                      artistsWithBase: newArtistsWithBase,
                      artistsWithExtra: newArtistsWithExtra,
                      totalPrice: newTotalPrice,
                      pricePerShow: newTotalPrice / newNumberOfShows,
                    },
                    newTotal: newOverallTotal,
                    distanceToTarget,
                  };
                }
              }
            } catch (err) {
              // Skip this stage if pricing fetch fails
              console.error(`Failed to fetch pricing for ${stage.stage}:`, err);
            }
          }

          // Apply the best addition if found
          if (bestAddition) {
            affordableStages[bestAddition.stageIdx].result = bestAddition.newResult;
            currentTotal = bestAddition.newTotal;
            improved = true;
          }
        }
      }

      // Check if in strict mode and over budget
      if (strictMode && currentTotal > totalBudget) {
        toast({
          title: "Cannot Match Budget",
          description: "Cannot create a plan that exactly matches your budget in strict mode. Try disabling strict mode or adjusting your budget.",
          variant: "destructive",
        });
        return;
      }

      // Create plan items for each stage
      const newPlanItems: PlanItem[] = affordableStages
        .filter(s => s.result !== null)
        .map(s => {
          const result = s.result!;
          return {
            id: `${Date.now()}-${Math.random()}`,
            careerStage: s.stage,
            numberOfShows: result.numberOfShows,
            numberOfArtists: result.numberOfArtists,
            showsPerArtist: result.baseShows,
            artistsWithBaseShows: result.artistsWithBase,
            artistsWithExtraShow: result.artistsWithExtra,
            packageTier,
            pricePerShow: Math.round(result.pricePerShow),
            totalPrice: result.totalPrice,
          };
        });

      // Add to plan
      setPlanItems([...planItems, ...newPlanItems]);

      // Show success toast
      const finalTotal = newPlanItems.reduce((sum, item) => sum + item.totalPrice, 0);
      const percentDiff = ((finalTotal - totalBudget) / totalBudget * 100).toFixed(1);
      const diffText = finalTotal > totalBudget
        ? `${percentDiff}% over budget`
        : `${Math.abs(parseFloat(percentDiff))}% under budget`;

      toast({
        title: "Plan Generated Successfully",
        description: `Created ${newPlanItems.length} plan item${newPlanItems.length > 1 ? 's' : ''} totaling ${formatPrice(finalTotal)} (${diffText})`,
      });

    } catch (err) {
      toast({
        title: "Generation Failed",
        description: "Failed to generate plan from budget. Please try again.",
        variant: "destructive",
      });
      console.error('Generate from budget error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Calculate display pricing for package cards (weighted totals)
  const getDisplayPricing = () => {
    if (!packagePricing) return null;

    const baseShows = Math.floor(numberOfShows / numberOfArtists);
    const remainder = numberOfShows % numberOfArtists;
    const artistsWithBase = numberOfArtists - remainder;
    const artistsWithExtra = remainder;

    return {
      setCore: calculateWeightedPrice('set-core', packagePricing, artistsWithBase, artistsWithExtra),
      setAudio: calculateWeightedPrice('set-audio', packagePricing, artistsWithBase, artistsWithExtra),
      setVideo: calculateWeightedPrice('set-video', packagePricing, artistsWithBase, artistsWithExtra),
    };
  };

  const displayPricing = getDisplayPricing();

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            MAXtimate™ Calculator
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Build a mock sponsorship plan with multiple artist career stages and show counts.
            Calculate package pricing and see comprehensive plan summary.
          </p>
        </div>

        {/* Top Section: Inputs and Plan Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 lg:items-stretch">
          {/* Input Form - Left Side (50% width) */}
          <div className="h-full">
            <Card className="h-full flex flex-col border-purple-200/50 overflow-hidden shadow-lg">
              {/* Gradient Header Section */}
              <div className="bg-gradient-to-br from-purple-500 to-blue-600 p-4 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-pulse opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="h-6 w-6" />
                    <h3 className="text-xl font-bold">Build Your Mock Plan</h3>
                  </div>
                  <p className="text-sm opacity-90">
                    Create plan items manually or generate from a budget
                  </p>
                </div>
              </div>

              <CardContent className="flex-1 flex flex-col p-5">
                <Tabs defaultValue="manual" className="flex-1 flex flex-col">
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="manual">
                      <Calculator className="h-4 w-4 mr-2" />
                      Manual Builder
                    </TabsTrigger>
                    <TabsTrigger value="budget">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Budget Builder
                    </TabsTrigger>
                  </TabsList>

                  {/* Manual Builder Tab */}
                  <TabsContent value="manual" className="flex-1 mt-0">
                    <div className="space-y-4 flex-1">
                      {/* Career Stage */}
                      <div className="space-y-2">
                        <Label htmlFor="careerStage" className="flex items-center gap-2 text-base font-semibold">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Music2 className="h-4 w-4 text-purple-600" />
                          </div>
                          Artist Career Stage
                        </Label>
                        <Select value={careerStage} onValueChange={setCareerStage}>
                          <SelectTrigger className="h-11 border-purple-200 focus:border-purple-400 focus:ring-purple-400">
                            <SelectValue placeholder="Select career stage" />
                          </SelectTrigger>
                          <SelectContent>
                            {careerStages.map((stage) => (
                              <SelectItem key={stage} value={stage}>
                                {stage}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Number of Shows - Manual Input */}
                      <div className="space-y-2">
                        <Label htmlFor="numberOfShows" className="flex items-center gap-2 text-base font-semibold">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Hash className="h-4 w-4 text-blue-600" />
                          </div>
                          Number of Shows
                        </Label>
                        <Input
                          id="numberOfShows"
                          type="number"
                          min={1}
                          max={100}
                          value={numberOfShows}
                          onChange={(e) => setNumberOfShows(parseInt(e.target.value) || 1)}
                          className="h-11 border-blue-200 focus:border-blue-400 focus:ring-blue-400 text-base"
                        />
                      </div>

                      {/* Number of Artists - Manual Input */}
                      <div className="space-y-2">
                        <Label htmlFor="numberOfArtists" className="flex items-center gap-2 text-base font-semibold">
                          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                            <Users className="h-4 w-4 text-teal-600" />
                          </div>
                          Number of Artists
                        </Label>
                        <Input
                          id="numberOfArtists"
                          type="number"
                          min={1}
                          max={20}
                          value={numberOfArtists}
                          onChange={(e) => setNumberOfArtists(parseInt(e.target.value) || 1)}
                          className="h-11 border-teal-200 focus:border-teal-400 focus:ring-teal-400 text-base"
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 pt-2">
                        <Button
                          onClick={handleCalculate}
                          disabled={isCalculating}
                          className="flex-1 h-12 text-base bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        >
                          {isCalculating ? (
                            <>
                              <MaxSpinner className="mr-2 h-5 w-5" />
                              Calculating...
                            </>
                          ) : (
                            <>
                              <Calculator className="mr-2 h-5 w-5" />
                              Calculate Pricing
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={resetCalculator}
                          disabled={isCalculating}
                          className="h-12 border-purple-200 hover:bg-purple-50"
                        >
                          Reset
                        </Button>
                      </div>

                      {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-600 text-sm">{error}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* Budget Builder Tab */}
                  <TabsContent value="budget" className="flex-1 mt-0">
                    <BudgetBuilder
                      careerStages={careerStages}
                      onGenerate={generateFromBudget}
                      isGenerating={isGenerating}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

          </div>

          {/* Plan Summary - Right Side (50% width) */}
          <div className="h-full">
            <PlanSummary planItems={planItems} />
          </div>
        </div>

        {/* Package Results - Full Width Below */}
        {packagePricing && displayPricing ? (
          <div className="space-y-8">
            {/* Package Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SimplifiedPackageCard
                  packageType="set-core"
                  price={displayPricing.setCore}
                  pricePerShow={Math.round(displayPricing.setCore / numberOfShows)}
                  showCount={numberOfShows}
                  onDetailsClick={setSelectedPackageForDetails}
                  onAddToPlan={() => addToPlan('set-core')}
                  isCalculating={isCalculating}
                />

                <SimplifiedPackageCard
                  packageType="set-audio"
                  price={displayPricing.setAudio}
                  pricePerShow={Math.round(displayPricing.setAudio / numberOfShows)}
                  showCount={numberOfShows}
                  onDetailsClick={setSelectedPackageForDetails}
                  onAddToPlan={() => addToPlan('set-audio')}
                  isCalculating={isCalculating}
                />

                <SimplifiedPackageCard
                  packageType="set-video"
                  price={displayPricing.setVideo}
                  pricePerShow={Math.round(displayPricing.setVideo / numberOfShows)}
                  showCount={numberOfShows}
                  onDetailsClick={setSelectedPackageForDetails}
                  onAddToPlan={() => addToPlan('set-video')}
                  isCalculating={isCalculating}
                />
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Card className="max-w-md mx-auto">
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Calculator className="w-12 h-12 mx-auto mb-4" />
                  <p>Select a career stage and click Calculate to see package pricing</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plan Sections - Only show if there are items in the plan */}
        {planItems.length > 0 && (
          <div className="space-y-8 mt-8">
            {/* Plan Items Table */}
            <PlanItemsList
              planItems={planItems}
              onRemoveItem={removeFromPlan}
              onUpdateItem={updatePlanItem}
              onClearAll={clearPlan}
            />

            {/* Package Deliverables Breakdown */}
            <PlanDeliverablesSimple planItems={planItems} />
          </div>
        )}

        {/* Call to Action for Unauthenticated Users */}
        {!user && (
          <div className="mt-12">
            <Card className="max-w-2xl mx-auto">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <h3 className="font-semibold text-gray-900">Ready to Create Plans?</h3>
                  <p className="text-sm text-gray-600">
                    Sign up for free to browse shows, save opportunities, and create sponsorship proposals.
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Link href="/register">
                      <Button size="sm">
                        Create Free Account
                      </Button>
                    </Link>
                    <Link href="/login">
                      <Button variant="outline" size="sm">
                        Sign In
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Package Details Modal */}
      {selectedPackageForDetails && (
        <PackageDetailsModal
          packageType={selectedPackageForDetails}
          isOpen={!!selectedPackageForDetails}
          onClose={() => setSelectedPackageForDetails(null)}
          selectedShowCount={numberOfShows}
        />
      )}

      {/* Career Stage Modal */}
      <CareerStageModal
        isOpen={isCareerStageModalOpen}
        onClose={() => setIsCareerStageModalOpen(false)}
        selectedStage={careerStage}
        onStageSelect={(stage) => {
          setCareerStage(stage);
          setIsCareerStageModalOpen(false);
        }}
      />

      {/* Show Count Modal */}
      <ShowCountModal
        isOpen={isShowCountModalOpen}
        onClose={() => setIsShowCountModalOpen(false)}
        selectedCount={numberOfShows}
        onCountSelect={(count) => {
          setNumberOfShows(count);
          setIsShowCountModalOpen(false);
        }}
      />
    </div>
    </>
  );
}